// / — the portal homepage: long-page rewrite of the legacy index page.
// Assembly owns modal + lightbox state; sections are pure presentational.
// The `#download` deep link reproduces the legacy INDEX_READY_JS auto-open.
import { useCallback, useEffect, useState } from "react";
import { HOME_NAV_LINKS, SiteHeader } from "../components/SiteHeader";
import { SiteFooter } from "../components/SiteFooter";
import { Hero } from "./home/Hero";
import { About } from "./home/About";
import { Gallery } from "./home/Gallery";
import { Donate } from "./home/Donate";
import { Help } from "./home/Help";
import { DownloadModal } from "./home/DownloadModal";
import { Lightbox } from "./home/Lightbox";

export function HomePage() {
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [imageIndex, setImageIndex] = useState<number | null>(null);

  // Legacy behavior: landing on /#download opens the connect modal.
  // Any other hash is a cross-page anchor jump (e.g. /terms → /#about): the
  // browser resolved it against an empty #root at parse time and gave up, so
  // the scroll has to be replayed once React has painted the sections.
  useEffect(() => {
    const hash = window.location.hash;
    if (hash === "" || hash === "#top") return;
    if (hash === "#download") {
      const t = setTimeout(() => setDownloadOpen(true), 500);
      return () => clearTimeout(t);
    }
    const raf = requestAnimationFrame(() => {
      document.querySelector(hash)?.scrollIntoView();
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  const openDownload = useCallback(() => setDownloadOpen(true), []);
  const closeDownload = useCallback(() => setDownloadOpen(false), []);
  const openImage = useCallback((i: number) => setImageIndex(i), []);
  const closeImage = useCallback(() => setImageIndex(null), []);

  return (
    <>
      <SiteHeader variant="overlay" links={HOME_NAV_LINKS} />
      <Hero onOpenDownload={openDownload} />
      <main>
        <About onOpenImage={openImage} />
        <Gallery onOpenImage={openImage} />
        <Donate />
        <Help />
      </main>
      <SiteFooter />
      <DownloadModal open={downloadOpen} onClose={closeDownload} />
      <Lightbox index={imageIndex} onClose={closeImage} onNavigate={setImageIndex} />
    </>
  );
}
